import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Link as MuiLink,
  Select,
  Slider,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import type { Template } from '../../types/template';
import NoCollateralCompanyInfo from './NoCollateralCompanyInfo';
import TemplateSelector from './TemplateSelector';

import type { QuickSettings as QuickSettingsType } from './types';

interface QuickSettingsProps {
  quickSettings: QuickSettingsType;
  onQuickSettingsChange: (newSettings: Partial<QuickSettingsType>) => void;
  selectedTemplates: Record<string, string>;
  onTemplateChange: (type: string, templateId: string) => void;
  availableTemplates: Template[];
  clientLanguage?: string | undefined;
  onClientLanguageChange?: (language: string) => void;
  onSaveAsDefault?: () => Promise<void>;
  isSavingAsDefault?: boolean;
  hasDraftGenerated?: boolean;
}

const EMPTY_OBJECT: Record<string, string> = {};

const QuickSettings: React.FC<QuickSettingsProps> = ({
  quickSettings,
  onQuickSettingsChange,
  selectedTemplates,
  onTemplateChange,
  availableTemplates,
  clientLanguage,
  onClientLanguageChange,
  onSaveAsDefault,
  isSavingAsDefault,
  hasDraftGenerated = false,
}) => {
  const theme = useTheme();
  const { account } = useAccount();
  const effectiveNoCollateral =
    (account?.clientSettings?.enableExternalClientMode ?? false) &&
    (quickSettings.noCollateral ?? false);

  const defaultLanguage = import.meta.env.VITE_PROJECT_ID?.includes('uk-')
    ? 'UK English'
    : import.meta.env.VITE_PROJECT_ID?.includes('us-')
      ? 'US English'
      : 'Australian English';

  const sliderStyles = useMemo(
    () => ({
      '& .MuiSlider-thumb': {
        '&:hover, &.Mui-focusVisible': {
          boxShadow: `0px 0px 0px 8px ${theme.palette.bidlogiq.brick}33`,
        },
        backgroundColor: theme.palette.bidlogiq.brick,
      },
      '& .MuiSlider-track': {
        backgroundColor: theme.palette.bidlogiq.brick,
      },
      '& .MuiSlider-rail': {
        backgroundColor: `${theme.palette.bidlogiq.brick}80`,
      },
      '& .MuiSlider-valueLabel': {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.bidlogiq.brick}`,
      },
      '&.Mui-disabled': {
        '& .MuiSlider-thumb': {
          backgroundColor: theme.palette.secondary.contrastText,
        },
        '& .MuiSlider-track': {
          backgroundColor: theme.palette.secondary.contrastText,
        },
        '& .MuiSlider-rail': {
          backgroundColor: `${theme.palette.secondary.contrastText}80`,
        },
      },
    }),
    [theme]
  );

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography
            variant="body1"
            color="text.primary"
            sx={{ fontSize: '1rem', fontWeight: 'bold' }}
          >
            Usage:
          </Typography>
          <Box
            component="ol"
            sx={{ mt: 1, mb: 0, pl: 2, color: 'text.secondary', fontSize: '0.9rem' }}
          >
            <li>Adjust settings to tailor the generated response.</li>
            <li>Changes are saved automatically.</li>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          {onClientLanguageChange && (
            <FormControl fullWidth>
              <InputLabel id="client-language-select-label">AI Language</InputLabel>
              <Select
                labelId="client-language-select-label"
                id="client-language-select"
                value={clientLanguage || defaultLanguage}
                label="AI Language"
                onChange={(e) => onClientLanguageChange(e.target.value)}
              >
                <MenuItem value="Australian English">Australian English</MenuItem>
                <MenuItem value="UK English">UK English</MenuItem>
                <MenuItem value="US English">US English</MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Controls container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Grid container spacing={4}>
            {/* Row 0: No Collateral Mode */}
            {(account?.clientSettings?.enableExternalClientMode ?? false) && (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    p: 2,
                    pb: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    backgroundColor: effectiveNoCollateral
                      ? `${theme.palette.warning.light}11`
                      : 'transparent',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={effectiveNoCollateral}
                          onChange={(e) => {
                            const isNoCollateral = e.target.checked;
                            const updates: Partial<typeof quickSettings> = {
                              noCollateral: isNoCollateral,
                            };
                            if (isNoCollateral) {
                              updates.llmTemperature = 2;
                              updates.caseStudyMode = 'off';
                            }
                            onQuickSettingsChange(updates);
                          }}
                          color="warning"
                        />
                      }
                      label={<Typography sx={{ fontWeight: 'bold' }}>External Client</Typography>}
                    />
                    <Tooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="body2">
                            Turn this on if you are generating a draft without any supporting
                            company documents. It forces the AI to rely on industry standards and
                            placeholders rather than searching your context.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        tabIndex={0}
                        sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }}
                      />
                    </Tooltip>
                  </Box>
                  <FormHelperText sx={{ fontSize: '.75rem', mt: 0, ml: 1, mb: 1 }}>
                    Overrides global company info with tender-specific details.
                  </FormHelperText>
                </Box>
              </Grid>
            )}

            {effectiveNoCollateral && (
              <Grid size={{ xs: 12 }}>
                <NoCollateralCompanyInfo
                  data={quickSettings.noCollateralCompanyInfo || EMPTY_OBJECT}
                  onChange={(newData) =>
                    onQuickSettingsChange({ noCollateralCompanyInfo: newData })
                  }
                />
              </Grid>
            )}

            {/* Row 1: Creativity & Word Count/Confidence */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography>
                  Creativity Level: <strong>{quickSettings.llmTemperature ?? 3}</strong>
                </Typography>
                <Tooltip
                  title={
                    effectiveNoCollateral ? (
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>1 - Outline:</strong> You fill in the blanks.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>2 - Industry standard approach:</strong> Some manual gaps.
                        </Typography>
                        <Typography variant="body2">
                          <strong>3 - Complete:</strong> State of the art.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Settings 1-3:</strong> Strict adherence to provided facts. May
                          leave intentional gaps for manual completion.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Settings 4-5:</strong> Expands on concepts and may utilise web
                          searches to automatically fill missing details.
                        </Typography>
                        <Typography
                          variant="body2"
                          color="warning.main"
                          sx={{ fontWeight: 'bold' }}
                        >
                          Warning: Always thoroughly review and verify any web-gathered information
                          before finalizing.
                        </Typography>
                      </Box>
                    )
                  }
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    tabIndex={0}
                    sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
              <Slider
                value={quickSettings.llmTemperature ?? 3}
                onChange={(_, val) => onQuickSettingsChange({ llmTemperature: val as number })}
                step={1}
                marks
                min={1}
                max={effectiveNoCollateral ? 3 : 5}
                valueLabelDisplay="auto"
                sx={sliderStyles}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1}
                sx={{ fontSize: '0.875rem' }}
              >
                {effectiveNoCollateral
                  ? quickSettings.llmTemperature === 1
                    ? '1 = Outline. You fill in the blanks.'
                    : quickSettings.llmTemperature === 2
                      ? '2 = Industry standard approach. Some manual gaps.'
                      : '3 = Complete, state of the art.'
                  : '1 = Strict/Factual, 5 = Creative/Expansive'}
              </Typography>{' '}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography>
                  Narrative Word Limit: <strong>{quickSettings.draftWordLimit ?? 10000}</strong> (
                  {Math.round((quickSettings.draftWordLimit ?? 10000) / 500)} pages*)
                </Typography>
                <Tooltip
                  title="Word count applies only to the core response generated from the outline. It does not include CVs, Case Studies, Executive Summary, or other attachments"
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    tabIndex={0}
                    sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
              <Slider
                value={quickSettings.draftWordLimit ?? 10000}
                onChange={(_, val) => onQuickSettingsChange({ draftWordLimit: val as number })}
                step={500}
                min={1000}
                max={20000}
                valueLabelDisplay="auto"
                sx={sliderStyles}
              />
            </Grid>

            {/* Row 3: Case Studies (Temporarily Disabled) */}
            {/* {!effectiveNoCollateral && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography gutterBottom>Case Study Display Mode (Experimental):</Typography>
                  <TripleToggle
                    value={quickSettings.caseStudyMode ?? 'off'}
                    onChange={(val) => onQuickSettingsChange({ caseStudyMode: val })}
                    labels={{
                      off: 'Off',
                      inline: 'Inline',
                      sections: 'Sections',
                    }}
                    descriptions={{
                      off: 'Do not include case studies.',
                      inline: 'Weave case studies into the response text (Experimental).',
                      sections: 'Append case studies as separate sections (Experimental).',
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography
                    gutterBottom
                    color={quickSettings.caseStudyMode === 'off' ? 'text.disabled' : 'text.primary'}
                  >
                    Min Case Studies to Include:{' '}
                    <strong>{quickSettings.minCaseStudies ?? 1}</strong>
                  </Typography>
                  <Slider
                    value={quickSettings.minCaseStudies ?? 1}
                    onChange={(_, val) => onQuickSettingsChange({ minCaseStudies: val as number })}
                    step={1}
                    marks
                    min={0}
                    max={5}
                    valueLabelDisplay="auto"
                    sx={sliderStyles}
                    disabled={quickSettings.caseStudyMode === 'off'}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography
                    gutterBottom
                    color={quickSettings.caseStudyMode === 'off' ? 'text.disabled' : 'text.primary'}
                  >
                    Max Case Studies to Include:{' '}
                    <strong>{quickSettings.maxCaseStudies ?? 3}</strong>
                  </Typography>
                  <Slider
                    value={quickSettings.maxCaseStudies ?? 3}
                    onChange={(_, val) => onQuickSettingsChange({ maxCaseStudies: val as number })}
                    step={1}
                    marks
                    min={0}
                    max={5}
                    valueLabelDisplay="auto"
                    sx={sliderStyles}
                    disabled={quickSettings.caseStudyMode === 'off'}
                  />
                </Grid>
              </>
            )} */}

            {/* Row 4: Template Selection */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TemplateSelector
                selectedTemplates={selectedTemplates}
                onTemplateChange={onTemplateChange}
                availableTemplates={availableTemplates}
                stacked={false}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {hasDraftGenerated ? (
                <Alert severity="warning">
                  A draft has already been generated. If you change the template, you will need to
                  generate the draft again to ensure all sections are filled correctly against the
                  new template.
                </Alert>
              ) : (
                <Alert severity="info">
                  Select a template to structure your generated response. You can view, create, and
                  upload custom templates in{' '}
                  <MuiLink
                    component={RouterLink}
                    to="/templates"
                    sx={{ fontWeight: 'bold', color: 'info.main' }}
                  >
                    Manage Templates
                  </MuiLink>
                  .
                </Alert>
              )}
            </Grid>

            {/* Row 5: Save Defaults */}
            {onSaveAsDefault && (
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveOutlinedIcon />}
                  onClick={onSaveAsDefault}
                  disabled={!!isSavingAsDefault}
                  sx={{
                    bgcolor: theme.palette.bidlogiq.brick,
                    color: 'white',
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: theme.palette.bidlogiq.rust,
                    },
                  }}
                >
                  Save as Default
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default QuickSettings;
